import * as React from "react"
import { ExternalLink, Heart, Mail, UserShield } from "lucide-react"

import { GenreFilters } from "@/components/genre-filters"
import { NoticeBanner } from "@/components/notice-banner"
import { RandomSongDialog } from "@/components/random-song-dialog"
import { SearchBar } from "@/components/search-bar"
import { SongGrid } from "@/components/song-grid"
import { SortSelect } from "@/components/sort-select"
import { ThemeToggle } from "@/components/theme-toggle"
import { Button } from "@/components/ui/button"
import { Separator } from "@/components/ui/separator"

import { config } from "@/config"

import { useFavorites } from "@/hooks/use-favorites"
import { useFilterState } from "@/hooks/use-filter-state"
import { useGenres, useSongs } from "@/hooks/use-songs"

import type { SortMode } from "@/lib/songs"

export function App() {
  const {
    state,
    setQuery,
    toggleGenre,
    clearGenres,
    setSort,
    setFavoritesOnly,
  } = useFilterState()
  const { favorites, toggleFavorite } = useFavorites()
  const [shuffleSeed, setShuffleSeed] = React.useState(() => Date.now())

  const favoriteIds = React.useMemo(
    () => Array.from(favorites),
    [favorites]
  )

  const filters = React.useMemo(
    () => ({
      query: state.query,
      genres: state.genres,
      sort: state.sort,
      seed: shuffleSeed,
      ids: state.favoritesOnly ? favoriteIds : undefined,
    }),
    [state.query, state.genres, state.sort, shuffleSeed, state.favoritesOnly, favoriteIds]
  )

  const randomFilters = React.useMemo(
    () => ({
      query: filters.query,
      genres: filters.genres,
      ids: filters.ids,
    }),
    [filters.query, filters.genres, filters.ids]
  )

  const {
    songs,
    total,
    loading,
    error,
    reload,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useSongs(filters)
  const { genres: allGenres, total: totalCount } = useGenres()

  const handleSortChange = (sort: SortMode) => {
    if (sort === "random") {
      setShuffleSeed(Date.now())
    }
    setSort(sort)
  }

  const renderSongsSection = () => {
    if (error) {
      return (
        <div className="flex flex-col items-center gap-3 py-24 text-center text-muted-foreground">
          <p className="text-lg font-medium text-foreground">
            곡 목록을 불러오지 못했어요
          </p>
          <p className="text-sm">{error}</p>
          <Button variant="outline" onClick={() => reload()}>
            다시 시도
          </Button>
        </div>
      )
    }

    if (loading) {
      return (
        <p className="py-24 text-center text-sm text-muted-foreground">
          곡 목록을 불러오는 중...
        </p>
      )
    }

    return (
      <>
        <p className="text-sm text-muted-foreground">총 {total}곡</p>
        <SongGrid
          songs={songs}
          favorites={favorites}
          onToggleFavorite={toggleFavorite}
          onReachEnd={hasNextPage ? fetchNextPage : undefined}
          isLoadingMore={isFetchingNextPage}
        />
      </>
    )
  }

  return (
    <div className="mx-auto flex min-h-svh max-w-7xl flex-col gap-6 px-4 py-6 sm:px-6 lg:px-8">
      <header className="flex flex-col gap-4">
        <div className="flex items-center justify-between gap-3">
          <div>
            <h1 className="text-xl font-semibold tracking-tight sm:text-2xl">
              {config.streamerName} 노래책
            </h1>

            <div className="flex items-center justify-center gap-4 pt-1 pl-1">
              <Button
                variant="link"
                className="p-0 text-muted-foreground underline-offset-4 hover:text-foreground hover:underline"
                asChild
              >
                <a
                  href={config.chzzkChannelUrl}
                  target="_blank"
                  rel="noreferrer"
                >
                  <ExternalLink className="size-4" />
                  치지직 채널 바로가기
                </a>
              </Button>

              <Separator orientation="vertical" className="my-2 h-4" />

              <Button
                variant="link"
                className="p-0 text-muted-foreground underline-offset-4 hover:text-foreground hover:underline"
                asChild
              >
                <a
                  href={config.songsApiUrl + "?action=admin"}
                  target="_blank"
                  rel="noreferrer"
                >
                  <UserShield className="size-4" />
                  관리자 로그인
                </a>
              </Button>
            </div>
          </div>
          <ThemeToggle />
        </div>

        <NoticeBanner />

        <p className="rounded-2xl bg-muted px-4 py-2.5 text-sm text-muted-foreground">
          치지직 채팅창에{" "}
          <span className="font-mono font-medium text-foreground">
            {config.commandPrefix} 아티스트-제목
          </span>{" "}
          형태로 붙여넣으면 신청돼요. 카드를 클릭하면 명령어가 자동으로
          복사됩니다.
        </p>
      </header>
      <div className="flex flex-col gap-3">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
          <SearchBar value={state.query} onChange={setQuery} />
          <div className="flex shrink-0 items-center gap-2">
            <SortSelect value={state.sort} onChange={handleSortChange} />
            <Button
              variant={state.favoritesOnly ? "default" : "outline"}
              size="icon"
              aria-label="즐겨찾기만 보기"
              aria-pressed={state.favoritesOnly}
              onClick={() => setFavoritesOnly(!state.favoritesOnly)}
            >
              <Heart className={state.favoritesOnly ? "fill-current" : ""} />
            </Button>
            <RandomSongDialog
              filters={randomFilters}
              disabled={state.favoritesOnly && favoriteIds.length === 0}
            />
          </div>
        </div>

        <GenreFilters
          genres={allGenres}
          totalCount={totalCount}
          selected={state.genres}
          onToggle={toggleGenre}
          onClear={clearGenres}
        />
      </div>

      {renderSongsSection()}
      <Separator orientation="horizontal" className="mt-4 mb-2" />
      <footer className="mb-4 space-y-2 text-center text-sm text-muted-foreground">
        <p className="inline-flex items-center justify-center">
          개발자에게 문의하기:{" "}
          <Button variant="link" className="m-0 h-auto py-0 pr-0 pl-2">
            <Mail className="size-4" />
            <a href="mailto:minsu.kim@lunaiz.com">minsu.kim@lunaiz.com</a>
          </Button>
        </p>

        <p>&copy; 2026 lunaiz. Co., Ltd. All rights reserved.</p>
      </footer>
    </div>
  )
}

export default App
